import { isElevarTrackingAllowed } from './consent.ts';
import { type ElevarEvent } from './types.ts';

declare global {
    interface Window {
        ElevarDataLayer?: ElevarEvent[];
        ElevarInvalidateContext?: () => void;
    }
}

const hasWindow = (): boolean => typeof window !== 'undefined';

if (hasWindow()) {
    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
}

// Elevar requires dl_user_data before action events after a context invalidation. User attributes
// resolve asynchronously, so action events fired in the meantime are held back rather than dropped.
let hasUserDataSinceContext = false;
const pendingElevarEvents: ElevarEvent[] = [];

export const hasPushedElevarUserData = (): boolean => hasUserDataSinceContext;

const ensureElevarDataLayer = (): ElevarEvent[] => {
    if (!Array.isArray(window.ElevarDataLayer)) {
        window.ElevarDataLayer = [];
    }
    return window.ElevarDataLayer;
};

export function pushElevarEvent(event: ElevarEvent): void {
    if (!hasWindow()) {
        return;
    }
    if (!isElevarTrackingAllowed()) {
        return;
    }

    const dataLayer = ensureElevarDataLayer();

    if (event.event === 'dl_user_data') {
        dataLayer.push(event);
        hasUserDataSinceContext = true;
        pendingElevarEvents.splice(0).forEach(queuedEvent => {
            dataLayer.push(queuedEvent);
        });
        return;
    }

    if (!hasUserDataSinceContext) {
        pendingElevarEvents.push(event);
        return;
    }

    dataLayer.push(event);
}

export function invalidateElevarContext(): void {
    hasUserDataSinceContext = false;
    pendingElevarEvents.length = 0;
    if (!hasWindow()) {
        return;
    }
    // eslint-disable-next-line new-cap -- global name is defined by Elevar's script
    window.ElevarInvalidateContext?.();
}
